import { useTheme } from '@emotion/react';
import {
  Box,
  Button,
  Chip,
  Grid,
  styled,
  TextField,
  useMediaQuery,
} from '@mui/material';
import FlexBox from 'components/flexbox/FlexBox';
import { Send } from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';

// Styled components
const StyledTextField = styled(TextField)(({ theme, isMobile }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    minHeight: isMobile ? '48px' : '80px',
    maxHeight: '100px',
    fontSize: '15.5px',
    fontWeight: 300,
    backgroundColor: '#FFFFFF',
    padding: '0px 0px !important',
    '& fieldset': {
      borderColor: 'transparent',
    },
    '&:hover fieldset': {
      borderColor: 'transparent',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'transparent',
    },
    '& textarea': {
      // padding: '12px 16px',
      padding: '0px 0px !important',
      lineHeight: '1.5',
      '&::placeholder': {
        color: '#B5B7C0',
        fontWeight: 300,
      },
    },
  },
}));

const CommentActions = styled(Box)(({ theme, isMobile }) => ({
  display: 'flex',
  alignItems: isMobile ? 'center' : 'flex-end',
  gap: '8px',
  marginTop: isMobile ? '8px' : '0',
  marginLeft: isMobile ? '0' : '16px',
  height: isMobile ? 'auto' : '36px',
  alignSelf: isMobile ? 'center' : 'flex-end',
  marginBottom: isMobile ? '0' : '9px',
}));

const MentionChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.darkBlue,
  color: '#FFFFFF',
  fontSize: '14px',
  fontWeight: 500,
  height: '24px',
  '& .MuiChip-label': {
    padding: '0 8px',
  },
  '& .MuiChip-deleteIcon': {
    color: '#FFFFFF',
    '&:hover': {
      color: '#FFFFFF',
      opacity: 0.8,
    },
  },
}));

const MentionSuggestion = styled(Box, {
  shouldForwardProp: prop => prop !== 'isSelected',
})(({ theme, isSelected }) => ({
  padding: '8px 16px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  backgroundColor: isSelected ? theme.palette.primary.darkBlue : 'transparent',
  color: isSelected ? '#FFFFFF' : 'inherit',
  '&:hover': {
    backgroundColor: isSelected ? theme.palette.primary.darkBlue : '#F5F5F5',
  },
}));

export default function CreateCommentBoxNew({
  smallBox = false,
  setShowCommentBox,
  onCommentSubmit,
  placeholder = '댓글을 입력하세요',
  users = [],
  editMode = false,
  editCommentData = null,
  onEditSubmit,
  onEditCancel,
}) {
  const [commentValue, setCommentValue] = useState('');
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearchText, setMentionSearchText] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const textFieldRef = useRef(null);

  // Default users if none provided
  const defaultUsers = [
    { id: 'chaeeun', name: '디자이너 채은', avatar: null },
    { id: 'user1', name: '라운지 초심자', avatar: null },
    { id: 'user2', name: '라리롬', avatar: null },
    { id: 'user3', name: '게임마스터', avatar: null },
  ];

  const userList = users.length > 0 ? users : defaultUsers;

  // Filter users based on search
  const filteredUsers = userList.filter(user =>
    user.name.toLowerCase().includes(mentionSearchText.toLowerCase())
  );

  const handleCommentChange = useCallback(e => {
    const value = e.target.value;
    setCommentValue(value);

    // Check for @ mentions
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const afterAt = value.substring(lastAtIndex + 1);
      const spaceIndex = afterAt.indexOf(' ');

      if (spaceIndex === -1) {
        // No space found, this is a mention search
        setMentionSearchText(afterAt);
        setShowMentionDropdown(true);
        setSelectedIndex(0);
      } else {
        // Space found, not a mention search
        setShowMentionDropdown(false);
        setMentionSearchText('');
      }
    } else {
      setShowMentionDropdown(false);
      setMentionSearchText('');
    }
  }, []);

  const handleMentionSelect = useCallback(
    user => {
      if (!user) return;

      // Remove focus immediately to prevent additional input
      if (textFieldRef.current) {
        textFieldRef.current.blur();
      }

      // Get the current value from the text field directly
      const currentValue = textFieldRef.current?.value || commentValue;

      // Find the last @ in the current value
      const lastAtIndex = currentValue.lastIndexOf('@');
      if (lastAtIndex === -1) return;

      // Get text before @
      const beforeAt = currentValue.substring(0, lastAtIndex);

      setCommentValue(beforeAt);

      // Clear the search text first
      setMentionSearchText('');
      setShowMentionDropdown(false);
      setSelectedIndex(0);

      // Update mentions
      setSelectedMentions(prev => [...prev, user]);
    },
    [commentValue]
  );

  const handleSubmit = useCallback(() => {
    if (commentValue.trim()) {
      if (editMode && editCommentData) {
        // Handle edit submission
        const editedData = {
          ...editCommentData,
          commentText: commentValue,
          mentions: selectedMentions,
          editedAt: new Date().toISOString(),
        };

        if (onEditSubmit) {
          onEditSubmit(editedData);
        }
      } else {
        // Handle regular comment submission
        const commentData = {
          text: commentValue,
          mentions: selectedMentions,
          timestamp: new Date().toISOString(),
        };

        if (onCommentSubmit) {
          onCommentSubmit(commentData);
        }
      }

      setCommentValue('');
      setSelectedMentions([]);
      setShowMentionDropdown(false);
      setMentionSearchText('');
    }
  }, [
    commentValue,
    selectedMentions,
    onCommentSubmit,
    editMode,
    editCommentData,
    onEditSubmit,
  ]);

  const handleKeyDown = useCallback(
    e => {
      if (showMentionDropdown && filteredUsers.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % filteredUsers.length);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(
            prev => (prev - 1 + filteredUsers.length) % filteredUsers.length
          );
        } else if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          const selectedUser = filteredUsers[selectedIndex];
          if (selectedUser) {
            handleMentionSelect(selectedUser);
          }
        } else if (e.key === 'Escape') {
          setShowMentionDropdown(false);
          setMentionSearchText('');
        }
      } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [
      showMentionDropdown,
      filteredUsers,
      selectedIndex,
      handleMentionSelect,
      handleSubmit,
    ]
  );

  const handleMentionDelete = useCallback(
    mentionId => {
      const mention = selectedMentions.find(m => m.id === mentionId);
      if (mention) {
        setSelectedMentions(prev => prev.filter(m => m.id !== mentionId));
        // Remove from comment text while preserving line breaks and formatting
        const mentionPattern = new RegExp(`@${mention.name}\\s*`, 'g');
        const newValue = commentValue.replace(mentionPattern, '');
        setCommentValue(newValue);
      }
    },
    [selectedMentions, commentValue]
  );

  // Populate input when in edit mode
  React.useEffect(() => {
    if (editMode && editCommentData) {
      setCommentValue(editCommentData.commentText || '');
      if (textFieldRef.current) {
        textFieldRef.current.focus();
      }
    }
  }, [editMode, editCommentData]);

  // Handle edit comment event
  React.useEffect(() => {
    const handleEditComment = event => {
      if (!editMode) {
        // Only handle if not already in edit mode
        const editData = event.detail;
        setCommentValue(editData.commentText || '');
        if (textFieldRef.current) {
          setTimeout(() => {
            textFieldRef.current.focus();
          }, 100);
        }
      }
    };

    document.addEventListener('edit-comment', handleEditComment);
    return () =>
      document.removeEventListener('edit-comment', handleEditComment);
  }, [editMode]);

  // Add mention programmatically (for reply functionality)
  React.useEffect(() => {
    const handleAddTag = event => {
      const targetUser = event.detail?.user || userList[0];
      if (targetUser && !selectedMentions.find(m => m.id === targetUser.id)) {
        setSelectedMentions(prev => [...prev, targetUser]);
        const newValue = commentValue.trim()
          ? `${commentValue} @${targetUser.name} `
          : `@${targetUser.name} `;
        setCommentValue(newValue);

        if (textFieldRef.current) {
          textFieldRef.current.focus();
        }
      }
    };

    document.addEventListener('add-reply-tag', handleAddTag);
    return () => document.removeEventListener('add-reply-tag', handleAddTag);
  }, [commentValue, selectedMentions, userList]);

  return (
    <Grid
      item
      xs={12}
      className="comment-box-container"
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: isMobile ? 0 : 3,
        border: '1px solid #E1E1E1',
        width: '100%',
        padding: isMobile ? 0 : 2,
        position: isMobile ? 'fixed' : 'relative',
        zIndex: isMobile ? 1000 : 'auto',
        boxShadow: 'none',
        overflow: 'visible',
        ...(isMobile && {
          bottom: '0px',
          left: 0,
          right: 0,
          borderTop: '1px solid #E1E1E1',
          borderLeft: 'none',
          borderRight: 'none',
          borderBottom: 'none',
          paddingBottom: 0,
        }),
      }}
    >
      <Grid
        container
        direction={'row'}
        mt={0.5}
        mb={0.5}
        px={isMobile ? 2 : 0}
        py={isMobile ? 0 : 0}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Grid mb={0} item sx={{ width: '100%' }}>
          <FlexBox alignItems="flex-start">
            <FlexBox
              className="comment-create-box-container"
              flexDirection="row"
              sx={{
                width: '100%',
                alignItems: isMobile ? 'center' : 'flex-end',
                m: 0,
                p: 0,
                minHeight: isMobile ? 'auto' : '80px',
                height: 'auto',
              }}
            >
              <Box sx={{ width: '100%', position: 'relative' }}>
                <StyledTextField
                  ref={textFieldRef}
                  multiline
                  minRows={1}
                  maxRows={4}
                  value={commentValue}
                  onChange={handleCommentChange}
                  onKeyDown={handleKeyDown}
                  placeholder={editMode ? '댓글을 수정하세요' : placeholder}
                  isMobile={isMobile}
                  fullWidth
                />

                {/* Mention suggestions dropdown */}
                {showMentionDropdown && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E1E1E1',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      zIndex: 9999,
                      maxHeight: isMobile ? '150px' : '200px',
                      overflowY: 'auto',
                      overflowX: 'visible',
                      // Mobile-specific positioning to prevent cropping
                      ...(isMobile && {
                        position: 'fixed',
                        top: 'auto',
                        bottom: '60px',
                        left: '16px',
                        right: '16px',
                        maxHeight: '200px',
                        zIndex: 9999,
                      }),
                    }}
                  >
                    {filteredUsers.map((user, index) => (
                      <MentionSuggestion
                        key={user.id}
                        isSelected={index === selectedIndex}
                        onClick={() => handleMentionSelect(user)}
                      >
                        {user.avatar && (
                          <img
                            src={user.avatar}
                            alt={user.name}
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                            }}
                          />
                        )}
                        <span>{user.name}</span>
                      </MentionSuggestion>
                    ))}
                    {filteredUsers.length === 0 && (
                      <Box sx={{ padding: '8px 16px', color: '#666' }}>
                        사용자를 찾을 수 없습니다
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              <CommentActions isMobile={isMobile}>
                {editMode && !isMobile && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setCommentValue('');
                      setSelectedMentions([]);
                      if (onEditCancel) {
                        onEditCancel();
                      }
                    }}
                    sx={{
                      minWidth: 'auto',
                      height: 'auto',
                      fontSize: '12.5px',
                      color: theme.palette.primary.darkBlue,
                      whiteSpace: 'nowrap',
                      mr: 1,
                      padding: '0px 0px 0px 4px',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    수정 취소
                  </Button>
                )}
                <Send
                  color={
                    commentValue.trim()
                      ? theme.palette.primary.darkBlue
                      : theme.palette.primary.grey900
                  }
                  size={20}
                  style={{
                    cursor: commentValue.trim() ? 'pointer' : 'default',
                    opacity: commentValue.trim() ? 1 : 0.5,
                  }}
                  onClick={commentValue.trim() ? handleSubmit : undefined}
                />
              </CommentActions>
            </FlexBox>
          </FlexBox>

          {/* Display selected mentions */}
          {selectedMentions.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedMentions.map(mention => (
                <MentionChip
                  key={mention.id}
                  label={`@${mention.name}`}
                  onDelete={() => handleMentionDelete(mention.id)}
                  size="small"
                />
              ))}
            </Box>
          )}
        </Grid>

        {/* {smallBox && isMobile && (
          <Grid item mt={1} sx={{ width: '100%' }}>
            <Button
              onClick={() => setShowCommentBox(false)}
              variant="text"
              fullWidth
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                height: '38px',
                fontSize: 15,
                color: theme.palette.primary.darkBlue,
                fontWeight: 500,
                padding: '0.6rem 0.5rem',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              수정 취소
            </Button>
          </Grid>
        )} */}
      </Grid>
    </Grid>
  );
}
